import { CardTypes, PaymentTypeMagento } from '../../checkout/shop-checkout.interface';
import { HE, snakeCaseToTitle } from '../../../common/utils/util';
import {
  LineItemStatus,
  OrderHistory as IOrderHistory,
  OrderHistory,
  UserOrderDetails,
} from '../order-history.interface';
import { ReturnStatus } from '../../return/interfaces/return-info.interface';

/**
 * Determines the badge style for the order line item based on the order and order details.
 *
 * @param order
 * @param orderDetail
 */
export function getBadgeStyleFromLineStatusItem(order: IOrderHistory, orderDetail: UserOrderDetails): string {
  if (order.returnInfo?.status === ReturnStatus.PENDING) {
    return 'bg-info text-dark';
  } else if (order.returnInfo?.status === ReturnStatus.APPROVED) {
    return 'bg-dark';
  }

  if (isHEOrder(orderDetail) && orderDetail.virtual) {
    switch (orderDetail.lineItemStatus) {
      case LineItemStatus.COMPLETE:
        return 'bg-success';
      case LineItemStatus.CANCELLED:
        return 'bg-danger';
      default:
        return 'bg-warning text-dark';
    }
  }

  switch (orderDetail.lineItemStatus) {
    case LineItemStatus.SHIPPED:
    case LineItemStatus.APPROVED:
      return 'bg-warning text-dark';
    case LineItemStatus.CANCELLED:
    case LineItemStatus.DENIED:
      return 'bg-danger';
    case LineItemStatus.COMPLETE:
      return 'bg-success';
    case LineItemStatus.PROCESSING:
    case LineItemStatus.SUBMITTED:
    case LineItemStatus.BACKORDERED:
      return 'bg-dark';
    default:
      return 'bg-warning text-dark';
  }
}

/**
 * Retrieves the payment method for the order
 *
 * @param order
 */
export function getPaymentMethod(order: OrderHistory): string {
  const paymentAdditionalInfo = order?.paymentAdditionalInfo;

  if (paymentAdditionalInfo?.payment_method !== PaymentTypeMagento.BRAINTREE_PREPAID) {
    return determinePaymentType(order);
  }

  switch (paymentAdditionalInfo?.payment_type) {
    case undefined:
      return order.paymentType;
    case PaymentTypeMagento.ANDROID_PAY_CARD:
      return CardTypes.GOOGLE_PAY;
    case PaymentTypeMagento.APPLE_PAY_CARD:
      return CardTypes.APPLE_PAY;
    default:
      return snakeCaseToTitle(paymentAdditionalInfo.payment_type);
  }
}

/**
 * Determines the UI status for the order based on the order and order details.
 *
 * @param order
 * @param orderDetail
 */
export function getOrderUiStatus(order: IOrderHistory, orderDetail: UserOrderDetails): string {
  if (order.returnInfo?.status === ReturnStatus.PENDING) {
    return 'Return Pending';
  } else if (order.returnInfo?.status === ReturnStatus.APPROVED) {
    return 'Return Approved';
  }

  if (isHEOrder(orderDetail) && orderDetail.virtual) {
    switch (orderDetail.lineItemStatus) {
      case LineItemStatus.COMPLETE:
        return 'Completed';
      case LineItemStatus.CANCELLED:
        return 'Cancelled';
      default:
        return 'In Progress';
    }
  }

  switch (orderDetail.lineItemStatus) {
    case LineItemStatus.SHIPPED:
      return 'Shipped';
    case LineItemStatus.PROCESSING:
    case LineItemStatus.SUBMITTED:
      return 'Ordered';
    case LineItemStatus.APPROVED:
      return 'Approved';
    case LineItemStatus.CANCELLED:
    case LineItemStatus.DENIED:
      return 'Cancelled';
    case LineItemStatus.BACKORDERED:
      return 'Backordered';
    case LineItemStatus.COMPLETE:
      return 'Completed';
    default:
      return 'In Progress';
  }
}

export function isHEOrder(orderDetail: UserOrderDetails) {
  const segment = orderDetail.productSegment;
  return segment && segment.toUpperCase() === HE;
}

export const CONNECT_PLUS_SHARPEN_FORMAT = 'Connect + Sharpen';

/**
 * Extracts the list of program source IDs from order line items.
 *
 * @param order
 */
export function getOrderProgramIds(order: OrderHistory): string[] {
  return order.userOrderDetails.map((item) => item.programSourceId ?? '');
}

export function determinePaymentType(order: OrderHistory) {
  if (order.paymentType?.toLowerCase() === 'local payments') {
    return getFundingSourceForLocalPayments(order?.fundingSource?.toLowerCase() || '');
  }
  return order.paymentType;
}

const getFundingSourceForLocalPayments = (fundingSource: string) => {
  switch (fundingSource) {
    case 'ideal':
      return 'iDEAL Pay';
    default:
      return fundingSource;
  }
}
